import React, { useState, useEffect, useCallback, useRef } from "react";
import DateRangeComponent from "../DateRangePicker";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ClearIcon from "@mui/icons-material/Clear";
import LoaderDotted from "../LoaderDotted/LoaderDotted";
import {
  getCurrentUser,
  getRoomRatePlan,
  getRoomType,
  checkInFromHotel,
  changeInfoForGroupCheckIn,
  getBookingInfo,
  getEmptyRoomNumber,
  getRoomInformationData,
} from "../../api-config/config";
import {
  formateDateInApiSupport,
  isEqualOrAfter,
} from "../../utils/formatTime";
import { calNoOfNights } from "../../utils/formatNumber";
import moment from "moment";
import "./GroupReservationForm.css";

const getTaxRateForBase = (basePrice: number, nights: number, roomCount = 1) => {
  const pricePerRoomPerNight =
    basePrice / (Math.max(nights, 1) * Math.max(roomCount, 1));
  return pricePerRoomPerNight > 7500 ? 18 : 5;
};

// ==================== TYPES ====================
interface RoomData {
  id: number;
  room_type_id: number;
  room_ids: number[];
  adults_per_room: number;
  rate_plan_id: number;
  no_of_rooms: number;
  first_name: string;
  last_name: string;
  email: string;
  mobile: string;
  children: number;
  price: string;
  final_price: string;
  total_price: string; // Change from number to string
  tax_amount: number;
  use_reverse_price?: boolean;
}

interface RoomTypeOption {
  id: number;
  room_type_name: string;
}

interface RatePlanOption {
  id: number;
  name: string;
  included: string;
}

interface AvailableRoom {
  id: number;
  room_name: string;
}

interface DateRange {
  startDate: Date;
  endDate: Date;
  key: string;
}

interface PriceInfo {
  total_sale_price: number;
  total_without_tax: number;
  total_tax: number;
  total_price_with_tax: number;
  room_service_charge: number;
  service_charge_percentage: number;
  discountAmount: number;
}

type RoomValidationErrors = Record<number, Record<string, string>>;

interface Props {
  bookedRooms?: any;
  calendarInfo: {
    startStr: string;
    endStr: string;
  };
  selectedRoomInfo?: any;
  handleCloseReservationDialog?: () => void;
  handleClose?: () => void;
  onFormSubmitted?: (data: any) => void;
  roomTypeList1?: any;
  groupIndBtn?: {
    status: boolean;
    btnType: string;
  };
}

const createDefaultRoomData = (id: number): RoomData => ({
  id,
  room_type_id: 0,
  room_ids: [],
  adults_per_room: 0,
  rate_plan_id: 0,
  no_of_rooms: 1,
  first_name: "",
  last_name: "",
  email: "",
  mobile: "",
  children: 0,
  price: "",
  final_price: "",
  total_price: "", // Change from 0 to ""
  tax_amount: 0,
  use_reverse_price: false,
});

const createDefaultPriceInfo = (): PriceInfo => ({
  total_sale_price: 0,
  total_without_tax: 0,
  total_tax: 0,
  total_price_with_tax: 0,
  room_service_charge: 0,
  service_charge_percentage: 0,
  discountAmount: 0,
});

const GroupReservationForm: React.FC<Props> = ({
  bookedRooms,
  calendarInfo,
  selectedRoomInfo,
  handleClose,
  handleCloseReservationDialog,
  onFormSubmitted,
  roomTypeList1,
  groupIndBtn,
}) => {
  const loggedUser = getCurrentUser();

  const [range, setRange] = useState<DateRange>({
    startDate: moment(calendarInfo.startStr).startOf("day").toDate(),
    endDate: moment(calendarInfo.endStr).startOf("day").toDate(),
    key: "selection",
  });

  const [rooms, setRooms] = useState<RoomData[]>([createDefaultRoomData(1)]);
  const [nextRoomId, setNextRoomId] = useState(2);
  const [roomTypeList, setRoomTypeList] = useState<RoomTypeOption[]>([]);
  const [roomRatePlans, setRoomRatePlans] = useState<Record<number, RatePlanOption[]>>({});
  const [availableRooms, setAvailableRooms] = useState<Record<number, AvailableRoom[]>>({});
  const [roomPrices, setRoomPrices] = useState<Record<number, PriceInfo>>({});
  const [roomInfoData, setRoomInfoData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Dropdown state keyed by room.id
  const [searchTerms, setSearchTerms] = useState<Record<number, string>>({});
  const [dropdownStates, setDropdownStates] = useState<Record<number, boolean>>({});
  const [validationErrors, setValidationErrors] = useState<RoomValidationErrors>({});
  const dropdownRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const fetchRoomTypes = useCallback(async () => {
    if (!loggedUser?.hotel_code) return;

    try {
      const res = await getRoomType(loggedUser.hotel_code, 1);
      if (res?.status_message === "success") {
        setRoomTypeList(res.data);
      }
    } catch (error) {
      console.error("Error fetching room types:", error);
    }
  }, [loggedUser?.hotel_code]);

  const fetchRatePlans = useCallback(async (roomId: number, roomTypeId: number) => {
    if (!loggedUser?.hotel_code || !roomTypeId) return;

    try {
      const response = await getRoomRatePlan({
        hotel_code: loggedUser.hotel_code,
        room_type_id: roomTypeId,
      });
      setRoomRatePlans(prev => ({
        ...prev,
        [roomId]: response?.data || [],
      }));
    } catch (error) {
      console.error("Error fetching rate plans:", error);
    }
  }, [loggedUser?.hotel_code]);

  const fetchAvailableRooms = useCallback(async (roomTypeId: number) => {
    if (!loggedUser?.hotel_code || !roomTypeId) return;

    try {
      const body = {
        from_date: formateDateInApiSupport(range.startDate),
        to_date: formateDateInApiSupport(range.endDate),
        pms_hotel_code: loggedUser.hotel_code,
        room_type_id: roomTypeId,
      };

      const response = await getEmptyRoomNumber(body);
      setAvailableRooms(prev => ({
        ...prev,
        [roomTypeId]: response?.data || [],
      }));
    } catch (error) {
      console.error("Error fetching available rooms:", error);
    }
  }, [loggedUser?.hotel_code, range.startDate, range.endDate]);

  // Fetch room information data (like in ReservationForm)
  const fetchRoomInformation = useCallback(async (adults: number, children = 0) => {
    if (!loggedUser?.hotel_code) return;

    try {
      const payload = {
        hotel_code: loggedUser.hotel_code,
        start_date: formateDateInApiSupport(range.startDate),
        end_date: formateDateInApiSupport(range.endDate),
        room_info: [
          {
            number_of_adults: adults,
            number_of_children: children,
            number_of_children_0_6: 0,
            number_of_children_6_12: 0,
          },
        ],
      };

      const response = await getRoomInformationData(payload);
      setRoomInfoData(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching room information:", error);
    }
  }, [loggedUser?.hotel_code, range.startDate, range.endDate]);

  // Calculate price for a room based on room information
  const calculateRoomPrice = useCallback((roomTypeId: number, room: RoomData, roomInfo: any) => {
    if (room.use_reverse_price) {
      return;
    }

    try {
      let basePrice = 0;
      let serviceCharge = 0;

      if (roomInfo?.rooms) {
        const roomTypeInfo = roomInfo.rooms.find((r: any) => r.room_type_id === roomTypeId);
        if (roomTypeInfo) {
          basePrice = roomTypeInfo.room_price?.total_price || 0;
          serviceCharge = roomTypeInfo.service_charge || 0;
        }
      }

      const nights = calNoOfNights(range.startDate, range.endDate);
      const totalSalePrice = basePrice * room.no_of_rooms;
      
      // Add rate plan amount if selected
      let ratePlanAmount = 0;
      if (room.rate_plan_id > 0) {
        const selectedPlan = roomRatePlans[room.id]?.find(p => p.id === room.rate_plan_id);
        if (selectedPlan) {
          ratePlanAmount = (selectedPlan as any).rate_amount || 0;
        }
      }

      const finalSalePrice = totalSalePrice + (ratePlanAmount * room.adults_per_room * nights);
      const roomServiceCharge = (finalSalePrice * serviceCharge) / 100;
      const priceWithService = finalSalePrice + roomServiceCharge;

      const roomCount = Math.max(room.no_of_rooms || room.room_ids.length || 1, 1);
      const taxRate = getTaxRateForBase(priceWithService, nights, roomCount);
      const tax = (priceWithService * taxRate) / 100;
      const finalPrice = priceWithService + tax;

      const priceInfo: PriceInfo = {
        total_sale_price: totalSalePrice,
        total_without_tax: priceWithService,
        total_tax: tax,
        total_price_with_tax: finalPrice,
        room_service_charge: roomServiceCharge,
        service_charge_percentage: serviceCharge,
        discountAmount: 0,
      };

      setRoomPrices(prev => ({
        ...prev,
        [room.id]: priceInfo,
      }));

      setRooms(prev => prev.map(r => {
        if (r.id === room.id) {
          const roundedBasePrice = Math.round(finalSalePrice);
          const roundedFinalPrice = Math.round(finalPrice);
          return {
            ...r,
            price: roundedBasePrice.toString(),
            final_price: roundedFinalPrice.toString(),
            total_price: roundedBasePrice.toString(),
            tax_amount: Math.round(tax),
          };
        }
        return r;
      }));
    } catch (error) {
      console.error("Error calculating room price:", error);
    }
  }, [range.startDate, range.endDate, roomRatePlans]);

  // Initial setup - fetch room types
  useEffect(() => {
    fetchRoomTypes();
  }, [fetchRoomTypes]);

  // Handle click outside for all dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      Object.entries(dropdownRefs.current).forEach(([roomIdStr, dropdownRef]) => {
        if (dropdownRef && !dropdownRef.contains(event.target as Node)) {
          const roomId = parseInt(roomIdStr);
          setDropdownStates(prev => ({
            ...prev,
            [roomId]: false,
          }));
        }
      });
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch room information when dates or guest count changes
  useEffect(() => {
    const firstRoom = rooms[0];
    if (firstRoom) {
      fetchRoomInformation(firstRoom.adults_per_room || 1, firstRoom.children || 0);
    }
  }, [range.startDate, range.endDate, fetchRoomInformation]);

  const handleAddRoom = () => {
    const newRoom = createDefaultRoomData(nextRoomId);
    setRooms(prev => [...prev, newRoom]);
    setNextRoomId(prev => prev + 1);
  };

  const handleRemoveRoom = (roomId: number) => {
    setRooms(prev => prev.filter(room => room.id !== roomId));
    setValidationErrors(prev => {
      const updated = { ...prev };
      delete updated[roomId];
      return updated;
    });
    setRoomRatePlans(prev => {
      const updated = { ...prev };
      delete updated[roomId];
      return updated;
    });
    setRoomPrices(prev => {
      const updated = { ...prev };
      delete updated[roomId];
      return updated;
    });
  };

  const handleRoomFieldChange = (roomId: number, fieldName: string, value: any) => {
    setValidationErrors(prev => ({
      ...prev,
      [roomId]: {
        ...(prev[roomId] || {}),
        [fieldName]: "",
      },
    }));

    setRooms(prev => prev.map(room => {
      if (room.id !== roomId) return room;

      const updatedRoom = { ...room, [fieldName]: value };

      if (fieldName === 'room_type_id' && value > 0) {
        fetchRatePlans(roomId, value);
        fetchAvailableRooms(value);
        updatedRoom.room_ids = [];
        updatedRoom.rate_plan_id = 0;
        // Fetch room info and auto-calculate price
        if (roomInfoData) {
          calculateRoomPrice(value, updatedRoom, roomInfoData);
        }
      }

      if (fieldName === 'room_ids' && Array.isArray(value)) {
        updatedRoom.room_ids = value;
        updatedRoom.no_of_rooms = value.length;
        if (roomInfoData && updatedRoom.room_type_id) {
          setTimeout(() => {
            calculateRoomPrice(updatedRoom.room_type_id, updatedRoom, roomInfoData);
          }, 0);
        }
      }

      if (fieldName === 'rate_plan_id' && value > 0) {
        updatedRoom.rate_plan_id = value;
        // Recalculate price when rate plan changes
        if (roomInfoData && updatedRoom.room_type_id) {
          calculateRoomPrice(updatedRoom.room_type_id, updatedRoom, roomInfoData);
        }
      }

      if (fieldName === 'total_price' && !updatedRoom.use_reverse_price) {
        return applyForwardPriceCalc(updatedRoom);
      }

      if (fieldName === 'final_price' && updatedRoom.use_reverse_price) {
        return applyReversePriceCalc(updatedRoom);
      }

      if ((fieldName === 'adults_per_room' || fieldName === 'children') && updatedRoom.room_type_id) {
        fetchRoomInformation(updatedRoom.adults_per_room, updatedRoom.children).then(data => {
          calculateRoomPrice(updatedRoom.room_type_id, updatedRoom, data);
        });
      }

      return updatedRoom;
    }));
  };

  const applyForwardPriceCalc = (room: RoomData): RoomData => {
    const basePrice = Math.max(0, Math.round(Number(room.total_price || room.price) || 0));
    const nights = calNoOfNights(range.startDate, range.endDate);
    const roomCount = Math.max(room.no_of_rooms || room.room_ids.length || 1, 1);
    const taxRate = getTaxRateForBase(basePrice, nights, roomCount);
    const tax = Math.round((basePrice * taxRate) / 100);
    const finalPrice = basePrice + tax;

    return {
      ...room,
      final_price: finalPrice.toString(),
      tax_amount: tax,
      price: basePrice.toString(),
      total_price: basePrice.toString(),
    };
  };

  const applyReversePriceCalc = (room: RoomData): RoomData => {
    const finalPrice = Math.max(0, Math.round(Number(room.final_price) || 0));
    const nights = calNoOfNights(range.startDate, range.endDate);
    const roomCount = Math.max(room.no_of_rooms || room.room_ids.length || 1, 1);

    if (finalPrice === 0) {
      return {
        ...room,
        price: "",
        total_price: "",
        tax_amount: 0,
        final_price: "",
      };
    }

    let basePrice = Math.round(finalPrice / 1.05);
    if (getTaxRateForBase(basePrice, nights, roomCount) === 18) {
      basePrice = Math.round(finalPrice / 1.18);
    }

    const tax = finalPrice - basePrice;

    return {
      ...room,
      price: basePrice.toString(),
      total_price: basePrice.toString(),
      tax_amount: tax,
      final_price: finalPrice.toString(),
    };
  };

  const handleReversePriceToggle = (roomId: number, enabled: boolean) => {
    setRooms(prev => prev.map(room => {
      if (room.id !== roomId) return room;

      const updatedRoom = { ...room, use_reverse_price: enabled };
      return enabled ? applyReversePriceCalc(updatedRoom) : applyForwardPriceCalc(updatedRoom);
    }));
  };

  function onSetRange(value: DateRange) {
    if (moment(value.startDate).isSame(value.endDate, "day")) {
      value.endDate = moment(value.startDate).add(1, "days").toDate();
    } else if (moment(value.endDate).isBefore(value.startDate, "day")) {
      value.startDate = moment(value.endDate).toDate();
      value.endDate = moment(value.startDate).add(1, "days").toDate();
    }
    setRange(value);
  }

  const canCheckInToday = isEqualOrAfter(
    moment().startOf("day"),
    moment(range.startDate).startOf("day")
  );

  const scrollToFirstValidationError = (errors: RoomValidationErrors) => {
    const roomIds = Object.keys(errors);
    if (!roomIds.length) return;

    const firstRoomId = roomIds[0];
    const firstField = Object.keys(errors[Number(firstRoomId)])[0];

    setTimeout(() => {
      const fieldEl = document.querySelector(
        `[data-validation-field="${firstRoomId}-${firstField}"]`
      );
      if (fieldEl) {
        fieldEl.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }

      const roomEl = document.querySelector(`[data-room-id="${firstRoomId}"]`);
      roomEl?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  };

  const validateRooms = () => {
    const errors: RoomValidationErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const mobileRegex = /^\d{10}$/;

    rooms.forEach(room => {
      const roomErrors: Record<string, string> = {};
      const priceValue = room.use_reverse_price
        ? room.final_price
        : (room.total_price || room.price);

      if (!room.room_type_id) roomErrors.room_type_id = "Please select room type";
      if (!room.room_ids.length) roomErrors.room_ids = "Please select room";
      if (!room.rate_plan_id) roomErrors.rate_plan_id = "Please select rate type";
      if (!room.adults_per_room) roomErrors.adults_per_room = "Please select adult";
      if (!room.first_name.trim()) roomErrors.first_name = "Please enter first name";
      if (!room.last_name.trim()) roomErrors.last_name = "Please enter last name";
      if (!room.email.trim()) {
        roomErrors.email = "Please enter email";
      } else if (!emailRegex.test(room.email.trim())) {
        roomErrors.email = "Please enter valid email";
      }
      if (!room.mobile.trim()) {
        roomErrors.mobile = "Please enter mobile number";
      } else if (!mobileRegex.test(room.mobile.trim())) {
        roomErrors.mobile = "Mobile number must be 10 digits";
      }
      if (
        priceValue === "" ||
        !Number.isFinite(Number(priceValue)) ||
        Number(priceValue) <= 0
      ) {
        if (room.use_reverse_price) {
          roomErrors.final_price = "Please enter valid final price";
        } else {
          roomErrors.total_price = "Please enter valid price";
        }
      }

      if (Object.keys(roomErrors).length) {
        errors[room.id] = roomErrors;
      }
    });

    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      scrollToFirstValidationError(errors);
      return false;
    }
    return true;
  };

  const buildGroupBookingPayload = (bookingStatus: string) => {
    const noOfNights = calNoOfNights(range.startDate, range.endDate);
    const totalRooms = rooms.reduce((total, room) => total + room.room_ids.length, 0);
    const grossAmount = rooms.reduce(
      (total, room) => total + (parseFloat(room.final_price) || 0),
      0
    );
    const totalSaleAmount = rooms.reduce(
      (total, room) => total + (parseFloat(room.price) || 0),
      0
    );
    const taxAmount = grossAmount - totalSaleAmount;
    const primaryRoom = rooms[0];
    const isSameGuestForAllRooms = rooms.every(
      room =>
        room.first_name === primaryRoom.first_name &&
        room.last_name === primaryRoom.last_name &&
        room.email === primaryRoom.email &&
        room.mobile === primaryRoom.mobile
    );
    const guestAssignmentType = isSameGuestForAllRooms ? "same_for_all" : "per_room";
    const primaryPlan = roomRatePlans[primaryRoom.id]?.find(
      plan => plan.id === primaryRoom.rate_plan_id
    );
    const ratePlanAmount = (primaryPlan as any)?.rate_amount || 0;

    const roomAssingedAdultInfo = rooms.flatMap(room =>
      room.room_ids.map(selectedRoomId => ({
        room_id: selectedRoomId,
        no_of_adults: room.adults_per_room,
        no_of_child: room.children,
        no_of_rooms: 1,
      }))
    );

    const roomBooking = rooms.flatMap(room => {
      const selectedRoomCount = room.room_ids.length || 1;
      const roomGrossAmount = (parseFloat(room.final_price) || 0) / selectedRoomCount;
      const roomTotalWithoutTax = (parseFloat(room.price) || 0) / selectedRoomCount;
      const roomTaxAmount = roomGrossAmount - roomTotalWithoutTax;

      return room.room_ids.map(selectedRoomId => ({
        room_id: selectedRoomId,
        room_type_id: room.room_type_id,
        no_of_pax: room.adults_per_room + room.children,
        no_of_adults: room.adults_per_room,
        no_of_children: room.children,
        no_of_rooms: 1,
        gross_amount: roomGrossAmount,
        total_sale_amount: roomTotalWithoutTax,
        discount_amount: 0,
        tax_amount: roomTaxAmount,
        total_without_tax: roomTotalWithoutTax,
        coupon_amount: 0,
        rate_plan_id: room.rate_plan_id,
        ...(guestAssignmentType === "per_room"
          ? {
              guest: {
                first_name: room.first_name,
                last_name: room.last_name,
                email: room.email,
                mobile_number: room.mobile,
                phone_number: room.mobile,
                other_members: [],
              },
            }
          : {}),
      }));
    });

    return {
      hotel_code: loggedUser.hotel_code,
      booking_type: "group",
      group_booking: true,
      guest_assignment_type: guestAssignmentType,
      from_date: formateDateInApiSupport(range.startDate),
      to_date: formateDateInApiSupport(range.endDate),
      first_name: primaryRoom.first_name,
      last_name: primaryRoom.last_name,
      email: primaryRoom.email,
      mobile_number: primaryRoom.mobile,
      phone_number: primaryRoom.mobile,
      country: "India",
      room_type_id: primaryRoom.room_type_id,
      rate_plan_id: primaryRoom.rate_plan_id,
      rate_plan_amount: ratePlanAmount,
      no_of_rooms: totalRooms,
      no_of_nights: noOfNights,
      gross_amount: grossAmount,
      total_sale_amount: totalSaleAmount,
      total_without_tax: totalSaleAmount,
      tax_amount: taxAmount,
      discount_amount: 0,
      paid_amount: 0,
      balance_amount: grossAmount,
      payment_mode: "",
      booking_status: bookingStatus,
      check_in_time:
        bookingStatus === "check_in"
          ? moment().format("YYYY-MM-DD HH:mm:ss")
          : null,
      minimum_advance: 0,
      booking_service: [],
      other_members: [],
      covered_services: "",
      room_assinged_adult_info: roomAssingedAdultInfo,
      room_booking: roomBooking,
    };
  };

  const getRoomSplitAmounts = (room: RoomData, selectedRoomCount: number) => {
    const roomGrossAmount = (parseFloat(room.final_price) || 0) / selectedRoomCount;
    const roomTotalWithoutTax = (parseFloat(room.price) || 0) / selectedRoomCount;
    const roomTaxAmount = roomGrossAmount - roomTotalWithoutTax;
    return { roomGrossAmount, roomTotalWithoutTax, roomTaxAmount };
  };

  const buildPostCreateCheckInPayload = (bookingIds: number[], groupBookingId: number) => {
    let bookingIdIndex = 0;

    const bookings = rooms.flatMap(room => {
      const selectedRoomCount = room.room_ids.length || 1;
      const selectedPlan = roomRatePlans[room.id]?.find(plan => plan.id === room.rate_plan_id);
      const ratePlanAmount = (selectedPlan as any)?.rate_amount || 0;

      return room.room_ids.map(selectedRoomId => {
        const bookingId = bookingIds[bookingIdIndex];
        bookingIdIndex += 1;
        const { roomGrossAmount, roomTotalWithoutTax, roomTaxAmount } =
          getRoomSplitAmounts(room, selectedRoomCount);

        return {
          booking_id: bookingId,
          from_date: formateDateInApiSupport(range.startDate),
          to_date: formateDateInApiSupport(range.endDate),
          booking_status: "check_in",
          first_name: room.first_name,
          last_name: room.last_name,
          email: room.email,
          mobile_number: room.mobile,
          phone_number: room.mobile,
          total_sale_amount: roomTotalWithoutTax,
          total_without_tax: roomTotalWithoutTax,
          tax_amount: roomTaxAmount,
          gross_amount: roomGrossAmount,
          room_booking_info: [
            {
              room_id: selectedRoomId,
              room_type_id: room.room_type_id,
              rate_plan_id: room.rate_plan_id,
              rate_plan_amount: ratePlanAmount,
              no_of_pax: room.adults_per_room + room.children,
              no_of_adults: room.adults_per_room,
              no_of_children: room.children,
              no_of_rooms: 1,
              total_sale_amount: roomTotalWithoutTax,
              total_without_tax: roomTotalWithoutTax,
              tax_amount: roomTaxAmount,
              gross_amount: roomGrossAmount,
              discount_amount: 0,
              coupon_amount: 0,
            },
          ],
        };
      });
    });

    return {
      group_booking_id: groupBookingId,
      from_date: formateDateInApiSupport(range.startDate),
      to_date: formateDateInApiSupport(range.endDate),
      bookings,
    };
  };

  const handleDone = async (bookingStatus = "pending") => {
    if (!loggedUser?.hotel_code) {
      console.error("User hotel code not found");
      return;
    }

    if (bookingStatus === "check_in" && !canCheckInToday) {
      alert("Check-in is not allowed for future dates");
      return;
    }

    if (!validateRooms()) {
      return;
    }

    setIsLoading(true);

    try {
      // Group do_booking_pms only supports pending; check-in uses change_info_for_group_check_in
      const payload = buildGroupBookingPayload("pending");
      console.log("Group reservation payload:", payload);

      const response = await checkInFromHotel(payload);
      console.log("Booking response:", response);

      if (response?.status_message === "failed") {
        alert(response?.message || "Booking failed");
        return;
      }

      let finalResponse = response;

      if (bookingStatus === "check_in") {
        const bookingIds = (Array.isArray(response?.data) ? response.data : [response?.data])
          .filter(Boolean)
          .map(Number);

        if (!bookingIds.length) {
          alert("Booking created but check-in failed: booking ids not returned");
          return;
        }

        const bookingInfo = await getBookingInfo(bookingIds[0]);
        const bookingData = bookingInfo?.data ?? bookingInfo;
        const groupBookingId =
          bookingData?.group_booking_id || bookingData?.group_id || 0;

        if (!groupBookingId) {
          alert("Booking created but check-in failed: group booking id not found");
          return;
        }

        const checkInPayload = buildPostCreateCheckInPayload(bookingIds, groupBookingId);
        console.log("Group check-in payload:", checkInPayload);

        const checkInResponse = await changeInfoForGroupCheckIn(checkInPayload);
        console.log("Group check-in response:", checkInResponse);

        if (checkInResponse?.status_message === "failed") {
          alert(checkInResponse?.message || "Check-in failed");
          return;
        }

        finalResponse = checkInResponse;
      }

      if (onFormSubmitted) {
        onFormSubmitted(finalResponse);
      } else {
        handleCloseReservationDialog?.();
        handleClose?.();
      }
    } catch (error: any) {
      console.error("Error submitting group reservation:", error);
      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message;
      alert(backendMessage || "Error submitting group reservation");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckIn = () => handleDone("check_in");

  const renderRoomForm = (room: RoomData, index: number) => {
    const ratePlans = roomRatePlans[room.id] || [];
    const availableRoomsList = room.room_type_id ? (availableRooms[room.room_type_id] || []) : [];
    const priceInfo = roomPrices[room.id] || createDefaultPriceInfo();

    const searchTerm = searchTerms[room.id] || "";
    const isDropdownOpen = dropdownStates[room.id] || false;
    const roomErrors = validationErrors[room.id] || {};

    const handleRoomSelection = (roomId: number) => {
      const isSelected = room.room_ids.includes(roomId);
      let updatedRoomIds: number[];
      
      if (isSelected) {
        updatedRoomIds = room.room_ids.filter(id => id !== roomId);
      } else {
        updatedRoomIds = [...room.room_ids, roomId];
      }
      
      handleRoomFieldChange(room.id, 'room_ids', updatedRoomIds);
    };

    const handleClearAll = () => {
      handleRoomFieldChange(room.id, 'room_ids', []);
      setSearchTerms(prev => ({
        ...prev,
        [room.id]: "",
      }));
    };

    const filteredRooms = availableRoomsList.filter(availRoom =>
      availRoom.room_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const selectedRoomNames = availableRoomsList
      .filter(availRoom => room.room_ids.includes(availRoom.id))
      .map(availRoom => availRoom.room_name);

    return (
      <div key={room.id} className="room-card">
        <div className="room-content">
          <div className="room-header">
            <span className="room-header-text">Room {index + 1}</span>
            {rooms.length > 1 && (
              <button
                onClick={() => handleRemoveRoom(room.id)}
                className="remove-button"
              >
                ×
              </button>
            )}
          </div>

          {isLoading && <LoaderDotted />}

          <div className={isLoading ? 'loading-overlay' : ''}>
            <div className="section-label">
              Room Details
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Room Type</label>
                <select
                  name="room_type_id"
                  className="form-input"
                  value={room.room_type_id}
                  onChange={(e) => {
                    handleRoomFieldChange(room.id, 'room_type_id', +e.target.value);
                    // Fetch room info and calculate price when room type changes
                    if (roomInfoData) {
                      calculateRoomPrice(+e.target.value, room, roomInfoData);
                    }
                  }}
                >
                  <option value={0}>Select</option>
                  {roomTypeList.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.room_type_name}
                    </option>
                  ))}
                </select>
                {roomErrors.room_type_id && (
                  <p className="validation-error">{roomErrors.room_type_id}</p>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Select Room(s)</label>
                <div 
                  ref={(el) => {
                    if (el) dropdownRefs.current[room.id] = el;
                  }}
                  className="custom-dropdown-container"
                >
                  <div className="dropdown-input-wrapper">
                    <div 
                      className="dropdown-input"
                      onClick={() => room.room_type_id && setDropdownStates(prev => ({
                        ...prev,
                        [room.id]: !prev[room.id],
                      }))}
                      style={{
                        cursor: room.room_type_id ? 'pointer' : 'not-allowed',
                        opacity: room.room_type_id ? 1 : 0.5,
                      }}
                    >
                      <div className="selected-rooms-container">
                        {selectedRoomNames.length > 0 ? (
                          selectedRoomNames.map((roomName, idx) => (
                            <div key={idx} className="room-chip-selected">
                              <span>{roomName}</span>
                            </div>
                          ))
                        ) : (
                          <span className="placeholder-text">Select...</span>
                        )}
                      </div>

                      <div className="dropdown-icons">
                        {room.room_ids.length > 0 && (
                          <button
                            type="button"
                            className="clear-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClearAll();
                            }}
                            title="Clear all selections"
                          >
                            <ClearIcon style={{ fontSize: '18px' }} />
                          </button>
                        )}
                        <span className={`arrow-icon ${isDropdownOpen ? 'open' : ''}`}>
                          ▼
                        </span>
                      </div>
                    </div>

                    {isDropdownOpen && room.room_type_id && (
                      <div className="dropdown-menu">
                        <div className="dropdown-search">
                          <input
                            type="text"
                            placeholder="Search..."
                            className="search-input"
                            value={searchTerm}
                            onChange={(e) => setSearchTerms(prev => ({
                              ...prev,
                              [room.id]: e.target.value,
                            }))}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>

                        <div className="dropdown-options">
                          {filteredRooms.length > 0 ? (
                            filteredRooms.map((availRoom) => (
                              <div
                                key={availRoom.id}
                                className={`dropdown-option ${
                                  room.room_ids.includes(availRoom.id) ? 'selected' : ''
                                }`}
                                onClick={() => handleRoomSelection(availRoom.id)}
                              >
                                <input
                                  type="checkbox"
                                  checked={room.room_ids.includes(availRoom.id)}
                                  onChange={(e) => {
                                    e.preventDefault();
                                    handleRoomSelection(availRoom.id);
                                  }}
                                  className="option-checkbox"
                                  style={{ pointerEvents: 'none' }}
                                />
                                <span className="option-label">
                                  {availRoom.room_name}
                                </span>
                              </div>
                            ))
                          ) : (
                            <div className="no-results">
                              No rooms found
                            </div>
                          )}
                        </div>

                        <div className="dropdown-footer">
                          <button
                            type="button"
                            className="select-all-btn"
                            onClick={() => {
                              const allRoomIds = filteredRooms.map(r => r.id);
                              handleRoomFieldChange(room.id, 'room_ids', allRoomIds);
                            }}
                          >
                            Select All
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {roomErrors.room_ids && (
                  <p className="validation-error">{roomErrors.room_ids}</p>
                )}
                {/* {room.room_ids.length > 0 && (
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                    Selected: {room.room_ids.length} room(s)
                  </p>
                )} */}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Rate Type</label>
                <select
                  name="rate_plan_id"
                  className="form-input"
                  value={room.rate_plan_id}
                  onChange={(e) => handleRoomFieldChange(room.id, 'rate_plan_id', +e.target.value)}
                >
                  <option value={0}>Select</option>
                  {ratePlans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {`${plan.name} : ${plan.included}`}
                    </option>
                  ))}
                </select>
                {roomErrors.rate_plan_id && (
                  <p className="validation-error">{roomErrors.rate_plan_id}</p>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Number Of Rooms</label>
                <input
                  type="number"
                  name="no_of_rooms"
                  className="form-input"
                  value={room.no_of_rooms}
                  disabled
                  style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                />
                {/* <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  Auto-calculated from selected rooms
                </p> */}
              </div>
            </div>

            <div className="section-label">
              Guest Details
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Adult</label>
                <select
                  name="adults_per_room"
                  className="form-input"
                  value={room.adults_per_room}
                  onChange={(e) => handleRoomFieldChange(room.id, 'adults_per_room', +e.target.value)}
                >
                  <option value={0}>Select</option>
                  <option value={1}>1</option>
                  <option value={2}>2</option>
                  <option value={3}>3</option>
                </select>
                {roomErrors.adults_per_room && (
                  <p className="validation-error">{roomErrors.adults_per_room}</p>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Child</label>
                <select
                  name="children"
                  className="form-input"
                  value={room.children}
                  onChange={(e) => handleRoomFieldChange(room.id, 'children', +e.target.value)}
                >
                  <option value={0}>0</option>
                  <option value={1}>1</option>
                  <option value={2}>2</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">First Name</label>
                <input
                  type="text"
                  name="first_name"
                  placeholder="Guest"
                  className="form-input"
                  value={room.first_name}
                  onChange={(e) => handleRoomFieldChange(room.id, 'first_name', e.target.value)}
                />
                {roomErrors.first_name && (
                  <p className="validation-error">{roomErrors.first_name}</p>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Last Name</label>
                <input
                  type="text"
                  name="last_name"
                  placeholder="Guest"
                  className="form-input"
                  value={room.last_name}
                  onChange={(e) => handleRoomFieldChange(room.id, 'last_name', e.target.value)}
                />
                {roomErrors.last_name && (
                  <p className="validation-error">{roomErrors.last_name}</p>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  name="email"
                  placeholder="guest@example.com"
                  className="form-input"
                  value={room.email}
                  onChange={(e) => handleRoomFieldChange(room.id, 'email', e.target.value)}
                />
                {roomErrors.email && (
                  <p className="validation-error">{roomErrors.email}</p>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Mobile</label>
                <input
                  type="tel"
                  name="mobile"
                  placeholder="Enter Mobile Number"
                  className="form-input"
                  value={room.mobile}
                  onChange={(e) => handleRoomFieldChange(room.id, 'mobile', e.target.value.replace(/\D/g, '').slice(0, 10))}
                />
                {roomErrors.mobile && (
                  <p className="validation-error">{roomErrors.mobile}</p>
                )}
              </div>
            </div>

            <div className="form-group reverse-calc-toggle">
              <label className="reverse-calc-label">
                <input
                  type="checkbox"
                  checked={!!room.use_reverse_price}
                  onChange={(e) => handleReversePriceToggle(room.id, e.target.checked)}
                />
                <span>Prices Include Tax</span>
              </label>
              {/* <p className="reverse-calc-hint">
                Enter final price and base price will be calculated after tax (5% or 18%)
              </p> */}
            </div>

            <div className="form-row price-fields-row">
              <div className="form-group">
                <label className="form-label">Price (Base)</label>
                <input
                  type="number"
                  name="total_price"
                  placeholder="3000"
                  className={`form-input ${room.use_reverse_price ? "form-input-readonly" : ""}`}
                  value={room.total_price || room.price}
                  readOnly={!!room.use_reverse_price}
                  disabled={!!room.use_reverse_price}
                  onChange={(e) => {
                    if (room.use_reverse_price) return;
                    const value = e.target.value;
                    if (/^\d*$/.test(value)) {
                      handleRoomFieldChange(room.id, 'total_price', value);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (!room.use_reverse_price && ["-", "+", ".", "e", "E"].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  min="0"
                  step="1"
                />
                {roomErrors.total_price && (
                  <p className="validation-error">{roomErrors.total_price}</p>
                )}
                {/* <p className="field-hint">
                  {room.use_reverse_price
                    ? `Auto-calculated with ${room.tax_amount > 0 && Number(room.total_price || room.price) > 0
                      ? Math.round((room.tax_amount / Number(room.total_price || room.price)) * 100)
                      : 0}% tax`
                    : "Editable - Final price updates automatically"}
                </p> */}
              </div>
              <div className="form-group">
                <label className="form-label">Final Price (incl. tax & svc. charge)</label>
                <input
                  type="number"
                  name="final_price"
                  placeholder="3150"
                  className={`form-input ${!room.use_reverse_price ? "form-input-readonly" : ""}`}
                  value={room.final_price}
                  readOnly={!room.use_reverse_price}
                  disabled={!room.use_reverse_price}
                  onChange={(e) => {
                    if (!room.use_reverse_price) return;
                    const value = e.target.value;
                    if (/^\d*$/.test(value)) {
                      handleRoomFieldChange(room.id, 'final_price', value);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (room.use_reverse_price && ["-", "+", ".", "e", "E"].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  min="0"
                  step="1"
                />
                {roomErrors.final_price && (
                  <p className="validation-error">{roomErrors.final_price}</p>
                )}
                {/* <p className="field-hint">
                  {room.use_reverse_price
                    ? "Editable - Base price updates automatically"
                    : `Auto-calculated with ${room.tax_amount > 0 && Number(room.price) > 0
                      ? Math.round((room.tax_amount / Number(room.price)) * 100)
                      : 0}% tax`}
                </p> */}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="group-reservation-container">
      <div className="date-section">
        <div className="date-label">Check-in : Check-out</div>
        <div className="date-input">
          <DateRangeComponent
            range={range}
            onSetRange={onSetRange}
            isShowPastDate={false}
            startFalse={false}
          />
        </div>
      </div>

      <div className="rooms-container">
        {rooms.map((room, index) => renderRoomForm(room, index))}

        <button onClick={handleAddRoom} className="add-room-button">
          <span className="add-room-icon">
            <AddCircleOutlineIcon style={{ fontSize: '20px' }} />
          </span>
          <span> Add Another Room Type</span>
        </button>

        <div className="form-footer">
          <div className="actions-container1">
            <button
              type="button"
              onClick={() => handleDone()}
              className="action-button reserve"
              disabled={isLoading}
            >
              {isLoading ? "Processing..." : "Reserve"}
            </button>
            {canCheckInToday && (
              <button
                type="button"
                onClick={handleCheckIn}
                className="action-button check-in"
                disabled={isLoading}
              >
                Check In
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupReservationForm;